import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import { appendSearchParams } from '@alexis/helpers/searchParam';
import { topHotelByTravelerType } from '@pages/HotelSearchResult/components/TravelerTypeBox/constants';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import { clsx } from '@wego/alexis/helpers/clsx';

import HotelMiniCard from '@components/hotels/HotelMiniCard';

import { notUndefinedAndNull } from '@helpers/array';

import ChevronRight from '@icons/chevron-right-big.svg';
import ChevronLeft from '@icons/chevron_left_big.svg';
import CloseIcon from '@icons/close_small.svg';

import {
  getHotelMetaSearchState,
  getIsRtlState,
  getTranslationsState,
  getUserState,
} from '@redux/selectors';

import { HotelMetaSearch } from '@wegoTypes/hotels/hotelMetaSearch';
import { Translations } from '@wegoTypes/translations';
import { User } from '@wegoTypes/user';

import styles from './TravelerTypeBox.module.scss';

const SCROLL_WIDTH = 296;
const REVIEWER_GROUP_NAME = ['BUSINESS', 'COUPLE', 'FAMILY', 'SOLO'] as const;

interface TravelerTypeBoxProps {
  pageViewId: string;
  wegoAnalyticsClientId?: string;
  wegoAnalyticsClientSessionId?: string;
  setIsOpen: (isOpen: boolean) => void;
}

const TravelerTypeBox: React.FC<TravelerTypeBoxProps> = ({
  pageViewId,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
  setIsOpen,
}) => {
  const containerDivRef = useRef<HTMLDivElement>(null);

  const hotelMetaSearch: HotelMetaSearch = useSelector(getHotelMetaSearchState);
  const isRtl = useSelector(getIsRtlState);
  const translations: Translations = useSelector(getTranslationsState);
  const user: User | null = useSelector(getUserState);

  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const [isItemOverflowed, setIsItemOverflowed] = useState(false);
  const [maxScrollLeft, setMaxScrollLeft] = useState(0);
  const [translateXPosition, setTranslateXPosition] = useState(0);
  const [travelerType, setTravelerType] =
    React.useState<(typeof REVIEWER_GROUP_NAME)[number]>('BUSINESS');

  const hotelLists = useMemo(() => {
    const city = topHotelByTravelerType.find(
      (hotel) => hotel.city_code === hotelMetaSearch.search?.cityCode,
    );
    if (city) {
      const hotelIds: number[] = city?.traveler_types?.[travelerType];
      const hotels = hotelMetaSearch.hotelViewModels
        .map((hotel) => {
          if (hotelIds.includes(hotel.hotelId) && hotel.filteredRates[0]) {
            return {
              hotel: {
                id: hotel.hotelId,
                name: hotel.hotel.name,
                star: hotel.hotel.star!,
                districtName: hotel.district?.name || '',
                bestRate: { price: hotel.filteredRates[0].price || {} },
                reviews: hotel.hotel.reviews,
                images: hotel.hotel.images,
              },
            };
          }
          return null;
        })
        .filter(notUndefinedAndNull);

      return hotels;
    }
  }, [hotelMetaSearch.hotelViewModels, travelerType]);

  const getReviewerGroupTranslation = (reviewerGroup: string) => {
    switch (reviewerGroup) {
      case 'BUSINESS':
        return translations.business_travellers as string;
      case 'COUPLE':
        return translations.couples as string;
      case 'FAMILY':
        return translations.families as string;
      case 'SOLO':
        return translations.solo as string;
      default:
        return '';
    }
  };

  const handlePrev = (): void => {
    if (wegoAnalyticsClientId && wegoAnalyticsClientSessionId) {
      const eventData = {
        id: pageViewId,
        category: 'dweb_traveler_type_box',
        object: 'search_results_page',
        action: 'click',
        value: 'previous',
      };
      genzoTrackActionEvent(
        API_BASE_URL,
        wegoAnalyticsClientId,
        wegoAnalyticsClientSessionId,
        user?.userHash,
        eventData,
      );
    }
    if (isRtl) {
      setTranslateXPosition(Math.max(translateXPosition - SCROLL_WIDTH, 0));
    } else {
      setTranslateXPosition(Math.min(translateXPosition + SCROLL_WIDTH, 0));
    }
  };

  const handleNext = (): void => {
    if (wegoAnalyticsClientId && wegoAnalyticsClientSessionId) {
      const eventData = {
        id: pageViewId,
        category: 'dweb_traveler_type_box',
        object: 'search_results_page',
        action: 'click',
        value: 'next',
      };
      genzoTrackActionEvent(
        API_BASE_URL,
        wegoAnalyticsClientId,
        wegoAnalyticsClientSessionId,
        user?.userHash,
        eventData,
      );
    }
    if (isRtl) {
      setTranslateXPosition(Math.min(translateXPosition + SCROLL_WIDTH, maxScrollLeft));
    } else {
      setTranslateXPosition(Math.max(translateXPosition - SCROLL_WIDTH, maxScrollLeft));
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    if (wegoAnalyticsClientId && wegoAnalyticsClientSessionId) {
      const eventData = {
        id: pageViewId,
        category: 'dweb_traveler_type_box',
        object: 'search_results_page',
        action: 'click',
        value: 'close',
      };
      genzoTrackActionEvent(
        API_BASE_URL,
        wegoAnalyticsClientId,
        wegoAnalyticsClientSessionId,
        user?.userHash,
        eventData,
      );
    }
  };

  const handleCardClick = (hotelId: number) => {
    if (wegoAnalyticsClientId && wegoAnalyticsClientSessionId) {
      const eventData = {
        id: pageViewId,
        category: 'dweb_traveler_type_box',
        object: 'search_results_page',
        action: 'click',
        value: hotelId.toString(),
      };
      genzoTrackActionEvent(
        API_BASE_URL,
        wegoAnalyticsClientId,
        wegoAnalyticsClientSessionId,
        user?.userHash,
        eventData,
      );
    }
  };

  const handleTravelerTypeClick = (type: (typeof REVIEWER_GROUP_NAME)[number]) => {
    setTravelerType(type);
    if (wegoAnalyticsClientId && wegoAnalyticsClientSessionId) {
      const eventData = {
        id: pageViewId,
        category: 'dweb_traveler_type_box',
        object: 'search_results_page',
        action: 'click',
        value: type,
      };
      genzoTrackActionEvent(
        API_BASE_URL,
        wegoAnalyticsClientId,
        wegoAnalyticsClientSessionId,
        user?.userHash,
        eventData,
      );
    }
  };

  useEffect(() => {
    const initializeScrollingValues = (): void => {
      if (!!containerDivRef.current && hotelLists?.length) {
        const scrollWidth = hotelLists.length * SCROLL_WIDTH;

        setIsItemOverflowed(scrollWidth > containerDivRef.current.clientWidth);
        setMaxScrollLeft((scrollWidth - containerDivRef.current.clientWidth) * (isRtl ? 1 : -1));
        setTranslateXPosition(0);
      }
    };

    window.addEventListener('resize', initializeScrollingValues);

    initializeScrollingValues();

    return () => {
      window.removeEventListener('resize', initializeScrollingValues);
    };
  }, [containerDivRef, isRtl, hotelLists]);

  useEffect(() => {
    const guests = hotelMetaSearch.search?.guestsCount || 0;
    if (hotelMetaSearch.search?.guestsCount && guests < 2) {
      setTravelerType('SOLO');
    } else if (guests < 3) {
      setTravelerType('COUPLE');
    } else {
      setTravelerType('FAMILY');
    }
  }, [hotelMetaSearch.search?.id]);

  useEffect(() => {
    const cityCodes = topHotelByTravelerType.map((hotel) => hotel.city_code);

    if (
      hotelMetaSearch.search?.cityCode &&
      cityCodes.includes(hotelMetaSearch.search.cityCode) &&
      hotelLists?.length
    ) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [hotelMetaSearch.search?.cityCode, hotelLists?.length]);

  return (
    <>
      {hotelLists?.length ? (
        <div className={styles.container}>
          <CloseIcon className={styles.closeIcon} onClick={handleClose} />
          <div>{translations.best_hotels_for_trip_type}</div>

          <div className={styles.travelerTypeWrapper}>
            <div className={styles.travelerTypes}>
              {REVIEWER_GROUP_NAME.map((type) => (
                <div
                  className={clsx(styles.type, travelerType === type && styles.active)}
                  key={type}
                  onClick={() => handleTravelerTypeClick(type)}
                >
                  {getReviewerGroupTranslation(type)}
                </div>
              ))}
            </div>

            <div className={styles.navigator}>
              <ChevronLeft
                className={clsx(
                  styles.icon,
                  (translateXPosition === 0 || !isItemOverflowed) && styles.disabled,
                )}
                onClick={handlePrev}
              />

              <ChevronRight
                className={clsx(
                  styles.icon,
                  (translateXPosition === maxScrollLeft || !isItemOverflowed) && styles.disabled,
                )}
                onClick={handleNext}
              />
            </div>
          </div>

          <div ref={containerDivRef} className={clsx(styles.hotelList)}>
            <div className={styles.scrollPane}>
              <div style={{ transform: `translateX(${translateXPosition}px)` }}>
                {hotelLists.map((hotel) => (
                  <HotelMiniCard
                    href={appendSearchParams(
                      `${window.location.origin}${pathname}/${hotel.hotel.id}`,
                      {
                        guests: searchParams.get('guests'),
                        search_id: hotelMetaSearch.search?.id,
                      },
                    )}
                    key={hotel.hotel.id}
                    similarHotelViewModel={hotel}
                    className={styles.hotelMiniCard}
                    onCardClick={() => handleCardClick(hotel.hotel.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default TravelerTypeBox;
